import { useEffect, useReducer, useRef } from "react";
import { observer } from "mobx-react-lite";
import { Modal } from "src/components/Modal";
import { useApi } from "src/contexts/api.context";
import { useActiveProjectQuery } from "src/hooks/useActiveProjectQuery";
import { useFlagIdentity } from "src/contexts/posthog.context";
import { useToaster } from "src/contexts/toaster.context";
import { ToastType } from "src/components/Toast";
import { generateAndDownloadSummaryPdf } from "src/utils/generateAndDownloadSummaryPdf";
import { StepMetadata } from "./StepMetadata";
import { StepOptions } from "./StepOptions";
import { StepPayment } from "./StepPayment";
import { initialWizardState, wizardReducer, type WizardStep } from "./types";
import type { World } from "src/types/World";
import type { MapState } from "src/types/MapState";

const STEP_LABELS: Record<WizardStep, string> = {
  1: "Report details",
  2: "Include in report",
  3: "Pay & export",
};

export const ExportDesignWizard = observer(function ExportDesignWizard(props: {
  isOpen: boolean;
  onClose: () => void;
  world: World;
  mapState: MapState;
  projectId: string | undefined;
  scenarioId: string | undefined;
  designId: string | undefined;
}) {
  const project = useActiveProjectQuery();
  const projectName = project.data?.name ?? "";
  const [state, dispatch] = useReducer(wizardReducer, projectName, initialWizardState);
  const { capture } = useFlagIdentity();
  const api = useApi();
  const toaster = useToaster();
  const pdfTriggered = useRef(false);

  // Default the report name to the project name each time the wizard opens.
  // Any edit the user makes during the session overrides it; reopening resets
  // back to the project name.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (props.isOpen && !wasOpen.current && projectName) {
      dispatch({ type: "SET_METADATA", patch: { name: projectName } });
    }
    wasOpen.current = props.isOpen;
  }, [props.isOpen, projectName]);

  // Post-payment: when the backend confirms `succeeded`, trigger the actual PDF
  // generation + download once, then close the modal. The ref guard prevents a
  // re-render from firing the download twice; it is reset by the parent when
  // the modal is closed and re-opened (the component is unmounted between
  // sessions because of `isOpen` driving the Portal).
  useEffect(() => {
    if (state.payment.status !== "succeeded") return;
    if (pdfTriggered.current) return;
    pdfTriggered.current = true;
    (async () => {
      try {
        const result = await generateAndDownloadSummaryPdf({
          world: props.world,
          projectId: props.projectId ?? "unknown",
          projectName: project.data?.name ?? "Tessa project",
          api,
          mapState: props.mapState,
          options: state.options,
          metadata: { name: state.metadata.name },
        });
        capture("export_pdf_downloaded", {
          projectId: props.projectId,
          scenarioId: props.scenarioId,
          designId: props.designId,
          paymentIntentId: state.payment.intentId,
          hasCarbon: result.hasCarbon,
          zonesCount: result.zonesCount,
          buildingsCount: result.buildingsCount,
        });
      } catch (err) {
        console.error("export wizard: PDF generation failed after payment:", err);
        toaster.show({
          text: "Payment succeeded but the PDF couldn't be generated. Contact support.",
          type: ToastType.DANGER,
          autoHide: true,
          id: crypto.randomUUID(),
        });
      } finally {
        props.onClose();
      }
    })();
  }, [
    state.payment.status,
    state.payment.intentId,
    state.options,
    state.metadata.name,
    props,
    api,
    project.data?.name,
    capture,
    toaster,
  ]);

  const cancel = () => {
    capture("export_wizard_cancelled", {
      atStep: state.step,
      projectId: props.projectId,
      scenarioId: props.scenarioId,
      designId: props.designId,
    });
    props.onClose();
  };

  const next = () => {
    capture("export_wizard_step_advanced", { fromStep: state.step, toStep: state.step + 1 });
    dispatch({ type: "NEXT" });
  };

  const back = () => dispatch({ type: "BACK" });

  const isNextDisabled = state.step === 1 && state.metadata.name.trim().length === 0;
  const isLastStep = state.step === 3;

  return (
    <Modal.Portal isOpen={props.isOpen}>
      <Modal.Card onClose={cancel}>
        <Modal.Header label={`Export design — ${STEP_LABELS[state.step]}`} onClose={cancel} />
        <div className="flex flex-row space-x-1 text-caption text-neutral-6">
          <span>Step {state.step} of 3</span>
        </div>
        <div className="py-2">
          {state.step === 1 && (
            <StepMetadata
              metadata={state.metadata}
              onChange={(patch) => dispatch({ type: "SET_METADATA", patch })}
            />
          )}
          {state.step === 2 && (
            <StepOptions
              options={state.options}
              onChange={(patch) => dispatch({ type: "SET_OPTIONS", patch })}
            />
          )}
          {state.step === 3 && (
            <StepPayment payment={state.payment} dispatch={dispatch} projectId={props.projectId} />
          )}
        </div>
        <Modal.Actions>
          <Modal.ActionCancelButton label="Cancel" onClick={cancel} />
          {state.step > 1 && <Modal.ActionCancelButton label="Back" onClick={back} />}
          {!isLastStep && (
            <Modal.ActionPrimaryButton label="Next" onClick={next} disabled={isNextDisabled} />
          )}
        </Modal.Actions>
      </Modal.Card>
    </Modal.Portal>
  );
});
